// /var/www/oszczedzam/client/src/components/goals/GoalProgress.js
import React from 'react';
import { 
  Box, Paper, Typography, LinearProgress, Grid, 
  Divider, Chip, CircularProgress
} from '@mui/material';
import { 
  TrendingUp as TrendingUpIcon,
  Flag as FlagIcon,
  CalendarToday as CalendarIcon,
  Category as CategoryIcon,
  CheckCircle as CheckCircleIcon,
  DonutLarge as DonutLargeIcon
} from '@mui/icons-material';

function GoalProgress({ goal }) {
  // Oblicz procent postępu
  const progress = Math.min(Math.round((goal.currentAmount / goal.targetAmount) * 100), 100);
  
  // Określ kolor paska postępu
  const getProgressColor = (percent) => {
    if (percent >= 100) return 'success';
    if (percent >= 75) return 'info';
    if (percent >= 50) return 'primary';
    if (percent >= 25) return 'warning';
    return 'error';
  };
  
  // Formatuj kwotę
  const formatAmount = (amount) => {
    return amount.toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' });
  };
  
  // Formatuj datę
  const formatDate = (dateString) => {
    if (!dateString) return 'Brak daty';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('pl-PL', options);
  };
  
  // Formatuj czas do osiągnięcia celu
  const formatTimeToGoal = () => {
    if (!goal.targetDate) return 'Brak terminu';
    
    const today = new Date();
    const target = new Date(goal.targetDate);
    
    // Jeśli data już minęła
    if (target < today) return 'Termin minął';
    
    const diffTime = Math.abs(target - today);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 30) {
      return `Zostało ${diffDays} dni`;
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return `Zostało ${months} ${months === 1 ? 'miesiąc' : months < 5 ? 'miesiące' : 'miesięcy'}`;
    } else {
      const years = Math.floor(diffDays / 365);
      const months = Math.floor((diffDays % 365) / 30);
      return `Zostało ${years} ${years === 1 ? 'rok' : years < 5 ? 'lata' : 'lat'}${months > 0 ? ` i ${months} ${months === 1 ? 'miesiąc' : months < 5 ? 'miesiące' : 'miesięcy'}` : ''}`;
    }
  };
  
  // Określ szacowany termin zakończenia (jeśli mamy historię wpłat)
  const estimateCompletionDate = () => {
    if (!goal.savingsHistory || goal.savingsHistory.length < 2) return 'Brak wystarczających danych';
    
    const history = [...goal.savingsHistory].sort((a, b) => new Date(a.date) - new Date(b.date));
    const firstDate = new Date(history[0].date);
    const lastDate = new Date(history[history.length - 1].date);
    
    // Oblicz średnią miesięczną wpłatę
    const totalDays = Math.max(1, Math.ceil((lastDate - firstDate) / (1000 * 60 * 60 * 24)));
    const totalSaved = history.reduce((sum, item) => sum + item.amount, 0);
    const avgDailySaving = totalSaved / totalDays;
    
    // Oblicz pozostałą kwotę
    const remaining = goal.targetAmount - goal.currentAmount;
    
    if (avgDailySaving <= 0) return 'Brak danych';
    
    // Oblicz szacowany czas do zakończenia
    const daysToCompletion = Math.ceil(remaining / avgDailySaving);
    const completionDate = new Date();
    completionDate.setDate(completionDate.getDate() + daysToCompletion);
    
    return formatDate(completionDate);
  };
  
  // Oblicz miesięczną kwotę potrzebną do osiągnięcia celu
  const calculateMonthlyAmount = () => {
    if (!goal.targetDate) return 'Brak daty docelowej';
    
    const today = new Date();
    const target = new Date(goal.targetDate);
    
    if (target <= today) return 'Termin minął';
    
    const diffTime = Math.abs(target - today);
    const diffMonths = Math.max(1, Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 30)));
    
    const remaining = goal.targetAmount - goal.currentAmount;
    if (remaining <= 0) return formatAmount(0);
    
    const monthlyAmount = remaining / diffMonths;
    return formatAmount(monthlyAmount);
  };
  
  // Pobierz kolor priorytetu
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };
  
  // Pobierz etykietę priorytetu
  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 'high': return 'Wysoki';
      case 'medium': return 'Średni';
      case 'low': return 'Niski';
      default: return 'Nieokreślony';
    }
  };

  return (
    <Paper elevation={1} sx={{ p: 3 }}>
      <Grid container spacing={3}>
        {/* Górna część z nazwą, kategorią i priorytetem */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" component="h2">
              {goal.name}
            </Typography>
            <Chip 
              label={getPriorityLabel(goal.priority)} 
              color={getPriorityColor(goal.priority)} 
              icon={<FlagIcon />}
            />
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <CategoryIcon sx={{ color: 'text.secondary', mr: 1 }} />
            <Typography variant="body1" color="text.secondary">
              {goal.category}
            </Typography>
          </Box>
          
          <Divider sx={{ mb: 3 }} />
        </Grid>

        {/* Wykres postępu */}
        <Grid item xs={12} md={8}>
          <Typography variant="h6" gutterBottom>
            Postęp
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography variant="body1" sx={{ mr: 2 }}>
              {formatAmount(goal.currentAmount)} z {formatAmount(goal.targetAmount)}
            </Typography>
            <Typography variant="body2" sx={{ ml: 'auto' }}>
              {progress}%
            </Typography>
          </Box>
          
          <LinearProgress 
            variant="determinate" 
            value={progress} 
            color={getProgressColor(progress)} 
            sx={{ height: 10, borderRadius: 5, mb: 3 }}
          />
          
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Pozostało do celu:
              </Typography>
              <Typography variant="body1">
                {formatAmount(Math.max(0, goal.targetAmount - goal.currentAmount))}
              </Typography>
            </Grid>
            
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Zalecana miesięczna wpłata:
              </Typography>
              <Typography variant="body1">
                {calculateMonthlyAmount()}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        
        {/* Informacje o czasie */}
        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Box sx={{ position: 'relative', display: 'inline-flex', flexDirection: 'column', alignItems: 'center' }}>
              <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                <CircularProgress 
                  variant="determinate" 
                  value={progress} 
                  size={120} 
                  thickness={5} 
                  color={getProgressColor(progress)} 
                />
                <Box
                  sx={{
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    position: 'absolute',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography variant="h4" component="div" color="text.secondary">
                    {progress}%
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                {goal.targetDate && (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      Termin realizacji:
                    </Typography>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <CalendarIcon sx={{ mr: 0.5, fontSize: 'small' }} />
                      {formatDate(goal.targetDate)}
                    </Typography>
                    <Typography variant="body2" fontWeight="medium" sx={{ mt: 0.5 }}>
                      {formatTimeToGoal()}
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
          </Box>
        </Grid>
        
        {/* Dodatkowe informacje */}
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          
          <Grid container spacing={2}>
            {goal.savingsHistory && goal.savingsHistory.length > 1 && (
              <Grid item xs={12} sm={6}>
                <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    <DonutLargeIcon sx={{ mr: 0.5, fontSize: 'small', verticalAlign: 'middle' }} />
                    Szacowany termin osiągnięcia:
                  </Typography>
                  <Typography variant="body1">
                    {estimateCompletionDate()}
                  </Typography>
                </Box>
              </Grid>
            )}
            
            {goal.description && (
              <Grid item xs={12} sm={6}>
                <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Opis celu:
                  </Typography>
                  <Typography variant="body2">
                    {goal.description}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
}

export default GoalProgress;